import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToWord,
  YearSelect,
  MonthSelect,
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import ExcelJS from 'exceljs';
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function FinancialStatement() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Variables 
  const [company, setCompany] = useState(null);
  // const today = new Date().toISOString().split("T")[0];
  const [monthFrom, setMonthFrom] = useState(null);
  const [monthTo, setMonthTo] = useState(null);
  const [year, setYear] = useState(null);
//#endregion






//#region Generate
  const fetchReportData = useCallback(async () => {
    const body = {
      monthFrom: monthFrom,
      monthTo: monthTo,
      year: year,
    }

    try {
      // const response = await axios.post(`${apiUrl}/api/Report/report-data/statement-of-account`, body, {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/financial-statement`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [monthFrom, monthTo, year]);

  // PDF
  const generateBSDetailed = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.balanceSheet;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/BS_DETAILED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 583, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 573, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 563, size: 9, font: calibriFont });

        targetPage.drawText(`${monthFrom} to ${monthTo}`, { x: 83, y: 506, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 495, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 446;
      const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 446;
      };

      for (const record of _data?.detailed) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        
        currentPage.drawText(`${record.accountType ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
        yPosition -= 10;

        for (const itemRow of record?.accounts) {
          // Draw Row Data
          // currentPage.drawText(FormatDateToMDY(order.date), { x: 35, y: yPosition, size: 9, font: calibriFont });
          const truncatedAccount = truncateText(itemRow?.glAccountName ?? "", 120, calibriFont, 8);
          currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

          currentPage.drawText(`${FormatValueToDecimal(itemRow.january)}`, { x: 200, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.february)}`, { x: 245, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.march)}`, { x: 295, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.april)}`, { x: 340, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.may)}`, { x: 384, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.june)}`, { x: 430, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.july)}`, { x: 474, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.august)}`, { x: 520, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.september)}`, { x: 570, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.october)}`, { x: 619, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.november)}`, { x: 669, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.december)}`, { x: 720, y: yPosition, size: 9, font: calibriFont });

          yPosition -= lineHeight;
        }

        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`Total ${record.accountType ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.january)}`, { x: 200, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.february)}`, { x: 245, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.march)}`, { x: 295, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.april)}`, { x: 340, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.may)}`, { x: 384, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.june)}`, { x: 430, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.july)}`, { x: 474, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.august)}`, { x: 520, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.september)}`, { x: 570, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.october)}`, { x: 619, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.november)}`, { x: 669, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.december)}`, { x: 720, y: yPosition, size: 9, font: calibriFont });
        yPosition -= 15;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `BS_DETAILED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateBSSummarized = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.balanceSheet;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/BS_SUMMARIZED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 756, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 746, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 736, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 90, y: 695, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 685.5, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 345, y: 654, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 630;
      const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      };

      for (const record of _data?.summary) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        currentPage.drawText(`${record.accountType ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
        yPosition -= 10;

        for (const itemRow of record?.accounts) {
          // Draw Row Data
          // currentPage.drawText(FormatDateToMDY(order.date), { x: 35, y: yPosition, size: 9, font: calibriFont });
          const truncatedAccount = truncateText(itemRow?.glAccountName ?? "", 120, calibriFont, 8);
          currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

          currentPage.drawText(`${FormatValueToDecimal(itemRow.amountsByYear[0]?.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });

          yPosition -= lineHeight;
        }

        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`Total ${record.accountType ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(record.totals[0]?.totalAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        yPosition -= 15;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `BS_SUMMARIZED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateISDetailed = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.incomeStatement;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/IS_DETAILED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 583, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 573, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 563, size: 9, font: calibriFont });

        targetPage.drawText(`${monthFrom} to ${monthTo}`, { x: 83, y: 506, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 495, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 446;
      const bottomThreshold = 80;
      const lineHeight = 20;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 446;
      };

      if (yPosition - lineHeight < bottomThreshold) {
        await addNewPage();
      }
        
      currentPage.drawText(`${_data?.detailed?.revenue?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      let revenueTotal = 0;
      let revenueTotalPercentage = 0;
      let revenueJanuary = 0;
      let revenueJanuaryPercentage = 0;
      let revenueFebruary = 0;
      let revenueFebruaryPercentage = 0;
      let revenueMarch = 0;
      let revenueMarchPercentage = 0;
      let revenueApril = 0;
      let revenueAprilPercentage = 0;
      let revenueMay = 0;
      let revenueMayPercentage = 0;
      let revenueJune = 0;
      let revenueJunePercentage = 0;
      let revenueJuly = 0;
      let revenueJulyPercentage = 0;
      let revenueAugust = 0;
      let revenueAugustPercentage = 0;
      let revenueSeptember = 0;
      let revenueSeptemberPercentage = 0;
      let revenueOctober = 0;
      let revenueOctoberPercentage = 0;
      let revenueNovember = 0;
      let revenueNovemberPercentage = 0;
      let revenueDecember = 0;
      let revenueDecemberPercentage = 0;

      for (const record of _data?.detailed?.revenue?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }
        
        const truncatedAccount = truncateText(record?.accountName ?? "", 100, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.total)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.amount)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.percent)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.amount)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.percent)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.amount)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.percent)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.amount)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.percent)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.amount)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.percent)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.amount)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.percent)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.amount)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.percent)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.amount)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.percent)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.amount)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.percent)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.amount)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.percent)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.amount)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.percent)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.amount)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.percent)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
        yPosition -= lineHeight;
        
        revenueTotal += record.total;
        revenueTotalPercentage += record.totalPercentage;
        revenueJanuary += record.monthly[0]?.amount;
        revenueJanuaryPercentage += record.monthly[0]?.percent;
        revenueFebruary += record.monthly[1]?.amount;
        revenueFebruaryPercentage += record.monthly[1]?.percent;
        revenueMarch += record.monthly[2]?.amount;
        revenueMarchPercentage += record.monthly[2]?.percent;
        revenueApril += record.monthly[3]?.amount;
        revenueAprilPercentage += record.monthly[3]?.percent;
        revenueMay += record.monthly[4]?.amount;
        revenueMayPercentage += record.monthly[4]?.percent;
        revenueJune += record.monthly[5]?.amount;
        revenueJunePercentage += record.monthly[5]?.percent;
        revenueJuly += record.monthly[6]?.amount;
        revenueJulyPercentage += record.monthly[6]?.percent;
        revenueAugust += record.monthly[7]?.amount;
        revenueAugustPercentage += record.monthly[7]?.percent;
        revenueSeptember += record.monthly[8]?.amount;
        revenueSeptemberPercentage += record.monthly[8]?.percent;
        revenueOctober += record.monthly[9]?.amount;
        revenueOctoberPercentage += record.monthly[9]?.percent;
        revenueNovember += record.monthly[10]?.amount;
        revenueNovemberPercentage += record.monthly[10]?.percent;
        revenueDecember += record.monthly[11]?.amount;
        revenueDecemberPercentage += record.monthly[11]?.percent;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;
      currentPage.drawText(`Total ${_data?.detailed?.revenue?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(revenueTotal)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueTotalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueJanuary)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueJanuaryPercentage)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueFebruary)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueFebruaryPercentage)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueMarch)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueMarchPercentage)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueApril)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueAprilPercentage)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueMay)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueMayPercentage)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueJune)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueJunePercentage)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueJuly)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueJulyPercentage)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueAugust)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueAugustPercentage)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueSeptember)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueSeptemberPercentage)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueOctober)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueOctoberPercentage)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueNovember)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueNovemberPercentage)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueDecember)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueDecemberPercentage)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
      yPosition -= 15;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;

      if (yPosition - lineHeight < bottomThreshold) {
        await addNewPage();
      }
        
      currentPage.drawText(`${_data?.detailed?.costOfSales?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      let costOfSalesTotal = 0;
      let costOfSalesTotalPercentage = 0;
      let costOfSalesJanuary = 0;
      let costOfSalesJanuaryPercentage = 0;
      let costOfSalesFebruary = 0;
      let costOfSalesFebruaryPercentage = 0;
      let costOfSalesMarch = 0;
      let costOfSalesMarchPercentage = 0;
      let costOfSalesApril = 0;
      let costOfSalesAprilPercentage = 0;
      let costOfSalesMay = 0;
      let costOfSalesMayPercentage = 0;
      let costOfSalesJune = 0;
      let costOfSalesJunePercentage = 0;
      let costOfSalesJuly = 0;
      let costOfSalesJulyPercentage = 0;
      let costOfSalesAugust = 0;
      let costOfSalesAugustPercentage = 0;
      let costOfSalesSeptember = 0;
      let costOfSalesSeptemberPercentage = 0;
      let costOfSalesOctober = 0;
      let costOfSalesOctoberPercentage = 0;
      let costOfSalesNovember = 0;
      let costOfSalesNovemberPercentage = 0;
      let costOfSalesDecember = 0;
      let costOfSalesDecemberPercentage = 0;

      for (const record of _data?.detailed?.costOfSales?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }
        
        const truncatedAccount = truncateText(record?.accountName ?? "", 100, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.total)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.amount)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.percent)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.amount)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.percent)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.amount)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.percent)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.amount)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.percent)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.amount)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.percent)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.amount)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.percent)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.amount)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.percent)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.amount)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.percent)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.amount)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.percent)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.amount)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.percent)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.amount)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.percent)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.amount)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.percent)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
        yPosition -= lineHeight;
        
        costOfSalesTotal += record.total;
        costOfSalesTotalPercentage += record.totalPercentage;
        costOfSalesJanuary += record.monthly[0]?.amount;
        costOfSalesJanuaryPercentage += record.monthly[0]?.percent;
        costOfSalesFebruary += record.monthly[1]?.amount;
        costOfSalesFebruaryPercentage += record.monthly[1]?.percent;
        costOfSalesMarch += record.monthly[2]?.amount;
        costOfSalesMarchPercentage += record.monthly[2]?.percent;
        costOfSalesApril += record.monthly[3]?.amount;
        costOfSalesAprilPercentage += record.monthly[3]?.percent;
        costOfSalesMay += record.monthly[4]?.amount;
        costOfSalesMayPercentage += record.monthly[4]?.percent;
        costOfSalesJune += record.monthly[5]?.amount;
        costOfSalesJunePercentage += record.monthly[5]?.percent;
        costOfSalesJuly += record.monthly[6]?.amount;
        costOfSalesJulyPercentage += record.monthly[6]?.percent;
        costOfSalesAugust += record.monthly[7]?.amount;
        costOfSalesAugustPercentage += record.monthly[7]?.percent;
        costOfSalesSeptember += record.monthly[8]?.amount;
        costOfSalesSeptemberPercentage += record.monthly[8]?.percent;
        costOfSalesOctober += record.monthly[9]?.amount;
        costOfSalesOctoberPercentage += record.monthly[9]?.percent;
        costOfSalesNovember += record.monthly[10]?.amount;
        costOfSalesNovemberPercentage += record.monthly[10]?.percent;
        costOfSalesDecember += record.monthly[11]?.amount;
        costOfSalesDecemberPercentage += record.monthly[11]?.percent;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;
      currentPage.drawText(`Total ${_data?.detailed?.costOfSales?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesTotal)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesTotalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJanuary)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJanuaryPercentage)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesFebruary)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesFebruaryPercentage)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMarch)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMarchPercentage)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesApril)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAprilPercentage)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMay)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMayPercentage)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJune)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJunePercentage)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJuly)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJulyPercentage)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAugust)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAugustPercentage)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesSeptember)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesSeptemberPercentage)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesOctober)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesOctoberPercentage)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesNovember)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesNovemberPercentage)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesDecember)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesDecemberPercentage)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
      yPosition -= 15;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;

      if (yPosition - lineHeight < bottomThreshold) {
        await addNewPage();
      }
        
      currentPage.drawText(`${_data?.detailed?.expenses?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      let expensesTotal = 0;
      let expensesTotalPercentage = 0;
      let expensesJanuary = 0;
      let expensesJanuaryPercentage = 0;
      let expensesFebruary = 0;
      let expensesFebruaryPercentage = 0;
      let expensesMarch = 0;
      let expensesMarchPercentage = 0;
      let expensesApril = 0;
      let expensesAprilPercentage = 0;
      let expensesMay = 0;
      let expensesMayPercentage = 0;
      let expensesJune = 0;
      let expensesJunePercentage = 0;
      let expensesJuly = 0;
      let expensesJulyPercentage = 0;
      let expensesAugust = 0;
      let expensesAugustPercentage = 0;
      let expensesSeptember = 0;
      let expensesSeptemberPercentage = 0;
      let expensesOctober = 0;
      let expensesOctoberPercentage = 0;
      let expensesNovember = 0;
      let expensesNovemberPercentage = 0;
      let expensesDecember = 0;
      let expensesDecemberPercentage = 0;

      for (const record of _data?.detailed?.expenses?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }
        
        const truncatedAccount = truncateText(record?.accountName ?? "", 100, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.total)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.amount)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.percent)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.amount)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.percent)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.amount)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.percent)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.amount)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.percent)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.amount)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.percent)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.amount)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.percent)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.amount)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.percent)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.amount)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.percent)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.amount)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.percent)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.amount)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.percent)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.amount)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.percent)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.amount)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.percent)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
        yPosition -= lineHeight;
        
        expensesTotal += record.total;
        expensesTotalPercentage += record.totalPercentage;
        expensesJanuary += record.monthly[0]?.amount;
        expensesJanuaryPercentage += record.monthly[0]?.percent;
        expensesFebruary += record.monthly[1]?.amount;
        expensesFebruaryPercentage += record.monthly[1]?.percent;
        expensesMarch += record.monthly[2]?.amount;
        expensesMarchPercentage += record.monthly[2]?.percent;
        expensesApril += record.monthly[3]?.amount;
        expensesAprilPercentage += record.monthly[3]?.percent;
        expensesMay += record.monthly[4]?.amount;
        expensesMayPercentage += record.monthly[4]?.percent;
        expensesJune += record.monthly[5]?.amount;
        expensesJunePercentage += record.monthly[5]?.percent;
        expensesJuly += record.monthly[6]?.amount;
        expensesJulyPercentage += record.monthly[6]?.percent;
        expensesAugust += record.monthly[7]?.amount;
        expensesAugustPercentage += record.monthly[7]?.percent;
        expensesSeptember += record.monthly[8]?.amount;
        expensesSeptemberPercentage += record.monthly[8]?.percent;
        expensesOctober += record.monthly[9]?.amount;
        expensesOctoberPercentage += record.monthly[9]?.percent;
        expensesNovember += record.monthly[10]?.amount;
        expensesNovemberPercentage += record.monthly[10]?.percent;
        expensesDecember += record.monthly[11]?.amount;
        expensesDecemberPercentage += record.monthly[11]?.percent;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;
      currentPage.drawText(`Total ${_data?.detailed?.expenses?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(expensesTotal)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesTotalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesJanuary)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesJanuaryPercentage)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesFebruary)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesFebruaryPercentage)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesMarch)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesMarchPercentage)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesApril)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesAprilPercentage)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesMay)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesMayPercentage)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesJune)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesJunePercentage)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesJuly)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesJulyPercentage)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesAugust)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesAugustPercentage)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesSeptember)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesSeptemberPercentage)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesOctober)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesOctoberPercentage)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesNovember)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesNovemberPercentage)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesDecember)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesDecemberPercentage)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
      yPosition -= 15;

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `IS_DETAILED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateISSummarized = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.incomeStatement;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/IS_SUMMARIZED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 756, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 746, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 736, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 90, y: 695, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 685.5, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 345, y: 654, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 630;
      // const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      };

      const value = _data.summary;

      currentPage.drawText(`REVENUE`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.revenue)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      currentPage.drawText(`DIRECT COST`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.directCost)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      var grossProfit =  value.revenue + value.directCost

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`GROSS PROFIT`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(grossProfit)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight;

      currentPage.drawText(`ADMINISTRATIVE EXPENSES`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.administrativeExpenses)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      currentPage.drawText(`OTHER INCOME`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.otherIncome)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      var netOperatingIncome = grossProfit + value.administrativeExpenses + value.otherIncome

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`NET OPERATING INCOME`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(netOperatingIncome )}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight;

      currentPage.drawText(`PROVISION FOR INCOME TAX`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.provisionForIncomeTax)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      var totalComprehensiveIncome = netOperatingIncome + value.provisionForIncomeTax

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`TOTAL COMPREHENSIVE INCOME`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(totalComprehensiveIncome )}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight;

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `IS_SUMMARIZED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateISSummarizedPercentage = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.incomeStatement;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/IS_SUMMARIZED_PERCENTAGE.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 756, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 746, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 736, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 90, y: 695, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 685.5, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 345, y: 654, size: 9, font: calibriFont });
      };

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 630;
      const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      };
      
      let revenueAmount = 0;
      let revenuePercentage = 0;

      currentPage.drawText(`${_data?.summaryPercentage?.revenue?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      for (const record of _data?.summaryPercentage?.revenue?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const truncatedAccount = truncateText(record?.accountName ?? "", 120, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.percentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
        yPosition -= lineHeight;

        revenueAmount += record.amount;
        revenuePercentage += record.percentage;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`Total ${_data?.summaryPercentage?.revenue?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(revenueAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenuePercentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
      yPosition -= 5;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;

      let costOfSalesAmount = 0;
      let costOfSalesPercentage = 0;

      currentPage.drawText(`${_data?.summaryPercentage?.costOfSales?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      for (const record of _data?.summaryPercentage?.costOfSales?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const truncatedAccount = truncateText(record?.accountName ?? "", 120, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.percentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
        yPosition -= lineHeight;

        costOfSalesAmount += record.amount;
        costOfSalesPercentage += record.percentage;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`Total ${_data?.summaryPercentage?.costOfSales?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesPercentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
      yPosition -= 5;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;

      let expensesAmount = 0;
      let expensesPercentage = 0;

      currentPage.drawText(`${_data?.summaryPercentage?.expenses?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      for (const record of _data?.summaryPercentage?.expenses?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const truncatedAccount = truncateText(record?.accountName ?? "", 120, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.percentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
        yPosition -= lineHeight;

        expensesAmount += record.amount;
        expensesPercentage += record.percentage;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`Total ${_data?.summaryPercentage?.expenses?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(expensesAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesPercentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
      yPosition -= 5;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `IS_SUMMARIZED_PERCENTAGE.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateCFSummarized = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.cashFlow;

    console.log({_data})

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/CASH_FLOW.pdf`;
      // const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      // const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 746, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 730, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 716, size: 9, font: calibriFont });

        targetPage.drawText(`${monthFrom} - ${monthTo} of ${year}`, { x: 110, y: 655, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 95, y: 636.5, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      // let yPosition = 630;
      // const bottomThreshold = 80;
      // const lineHeight = 12;

      // Helper to handle page breaks
      /* const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      }; */

      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.netIncome)}`, { x: 450, y: 581, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.depreciation)}`, { x: 450, y: 543, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeInventory)}`, { x: 450, y: 509, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeEmployeeAdvances)}`, { x: 450, y: 490, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeReceivables)}`, { x: 450, y: 471, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeOtherAssets)}`, { x: 450, y: 452, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeCurrentLiabilities)}`, { x: 450, y: 433, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.totalOperating)}`, { x: 450, y: 418, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.purchaseEquipment)}`, { x: 450, y: 363, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.purchaseBuilding)}`, { x: 450, y: 345, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.purchaseRenovations)}`, { x: 450, y: 328, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.totalInvesting)}`, { x: 450, y: 310, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.proceedsLoan)}`, { x: 450, y: 234, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.proceedsShareholders)}`, { x: 450, y: 215, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.issuanceShareCapital)}`, { x: 450, y: 198, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.totalFinancing)}`, { x: 450, y: 180, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.netIncreaseDecrease)}`, { x: 450, y: 143, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.cashBeginning)}`, { x: 450, y: 125, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.cashEnding)}`, { x: 450, y: 107, size: 9, font: calibriFont });

      currentPage.drawText(`${monthFrom} 31, ${year}`, { x: 153, y: 125, size: 9, font: calibriFont });
      currentPage.drawText(`${monthTo} 31, ${year}`, { x: 128, y: 107, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.cashEnding)}`, { x: 450, y: 68, size: 9, font: calibriFont });

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `CASH_FLOW.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const exportWorkingTrialBalanceExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const _data = allData;

      if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Working Trial Balance');

      // 2. Add Company Header Information
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); // Gap

      // 3. Add Report Title and Filters
      worksheet.addRow(['WORKING TRIAL BALANCE']).font = { bold: true, size: 12 };
      worksheet.addRow([`Period: ${monthFrom} to ${monthTo} ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Gap

      // 4. Define Table Columns
      const headerRow = worksheet.addRow([
        'Account Name',
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ]);

      // Style the Header Row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { 
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' } 
        };
      });

      // 5. Add Data Rows
      let totals = {
        jan: 0, feb: 0, mar: 0, apr: 0, may: 0, jun: 0,
        jul: 0, aug: 0, sep: 0, oct: 0, nov: 0, dec: 0
      };

      _data.forEach(record => {
        const row = worksheet.addRow([
          record.glAccountName ?? "N/A",
          parseFloat(record.january || 0),
          parseFloat(record.february || 0),
          parseFloat(record.march || 0),
          parseFloat(record.april || 0),
          parseFloat(record.may || 0),
          parseFloat(record.june || 0),
          parseFloat(record.july || 0),
          parseFloat(record.august || 0),
          parseFloat(record.september || 0),
          parseFloat(record.october || 0),
          parseFloat(record.november || 0),
          parseFloat(record.december || 0),
        ]);

        // Numeric formatting for columns B to M (index 2 to 13)
        for (let i = 2; i <= 13; i++) {
          row.getCell(i).numFmt = '#,##0.00';
        }

        // Accumulate Grand Totals
        totals.jan += (record.january || 0);
        totals.feb += (record.february || 0);
        totals.mar += (record.march || 0);
        totals.apr += (record.april || 0);
        totals.may += (record.may || 0);
        totals.jun += (record.june || 0);
        totals.jul += (record.july || 0);
        totals.aug += (record.august || 0);
        totals.sep += (record.september || 0);
        totals.oct += (record.october || 0);
        totals.nov += (record.november || 0);
        totals.dec += (record.december || 0);
      });

      // 6. Grand Totals Footer Row
      const grandTotalRow = worksheet.addRow([
        'GRAND TOTAL',
        totals.jan, totals.feb, totals.mar, totals.apr, totals.may, totals.jun,
        totals.jul, totals.aug, totals.sep, totals.oct, totals.nov, totals.dec
      ]);

      grandTotalRow.font = { bold: true };
      // Style and Format Grand Total numeric cells
      for (let i = 2; i <= 13; i++) {
        const cell = grandTotalRow.getCell(i);
        cell.numFmt = '#,##0.00';
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } }; // Yellow highlight
        cell.border = { 
          top: { style: 'thin' }, 
          bottom: { style: 'double' } 
        };
      }

      // 7. Adjust Column Widths
      worksheet.columns = [
        { width: 35 }, // Account Name
        { width: 12 }, // Jan
        { width: 12 }, // Feb
        { width: 12 }, // Mar
        { width: 12 }, // Apr
        { width: 12 }, // May
        { width: 12 }, // Jun
        { width: 12 }, // Jul
        { width: 12 }, // Aug
        { width: 12 }, // Sep
        { width: 12 }, // Oct
        { width: 12 }, // Nov
        { width: 12 }, // Dec
      ];

      // 8. Generate and Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Working_Trial_Balance_${year}_${new Date().getTime()}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasFinancialStatement) {
    return <NoAccess />;
  }
//#endregion






//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Financial Statement`}
            </h2>
            <p className="text-gray-500 mt-1">Financial performance and financial position.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Year
            </label>
            <YearSelect data={year} setData={setYear} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Month From
            </label>
            <MonthSelect data={monthFrom} setData={setMonthFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Month To
            </label>
            <MonthSelect data={monthTo} setData={setMonthTo} isEditable={true}/>
          </div>

        </div>

        <div className="mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 px-2">Generate Reports</h2>

          {/* Header Row - Hidden on Mobile */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-xs font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-2">Report Category</div>
            <div className="col-span-5">PDF Reports</div>
            <div className="col-span-5 border-l border-gray-100">Excel Reports</div>
          </div>

          <div className="space-y-3">
            {[
              { 
                label: "Balance Sheet", 
                pdf_detailed: generateBSDetailed, 
                pdf_summarized: generateBSSummarized, 
                excel_detailed: exportWorkingTrialBalanceExcel, // Replace with your Excel function
                excel_summarized: exportWorkingTrialBalanceExcel, // Replace with your Excel function
              },
              { 
                label: "Income Statement", 
                pdf_detailed: generateISDetailed, 
                pdf_summarized: generateISSummarized, 
                pdf_extra: generateISSummarizedPercentage, 
                excel_detailed: exportWorkingTrialBalanceExcel, // Replace with your Excel function
                excel_summarized: exportWorkingTrialBalanceExcel, // Replace with your Excel function
                excel_extra: exportWorkingTrialBalanceExcel, // Replace with your Excel function
              },
              { 
                label: "Cash Flow", 
                pdf_summarized: generateCFSummarized, 
                excel_summarized: exportWorkingTrialBalanceExcel, // Replace with your Excel function
              },
            ].map((group, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                
                {/* Label */}
                <div className="col-span-2">
                  <span className="text-sm font-bold text-gray-700">{group.label}</span>
                </div>

                {/* PDF Column */}
                <div className="col-span-5 flex flex-wrap gap-2">
                  {group.pdf_detailed &&
                    <button 
                      onClick={group.pdf_detailed} 
                      className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 transition-all active:scale-95"
                      title="Download PDF Detailed"
                    >
                      <RestartAltIcon sx={{ fontSize: 16 }} /> Detailed
                    </button>
                  }
                  
                  {group.pdf_summarized &&
                    <button 
                      onClick={group.pdf_summarized} 
                      className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all active:scale-95"
                    >
                      Summarized
                    </button>
                  }

                  {group.pdf_extra && (
                    <button 
                      onClick={group.pdf_extra} 
                      className="flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-100 transition-all active:scale-95"
                    >
                      Summarized w/ %
                    </button>
                  )}
                </div>

                {/* Excel Column */}
                <div className="col-span-5 flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0">
                  {group.excel_detailed &&
                    <button 
                      onClick={group.excel_detailed} 
                      className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 shadow-sm transition-all active:scale-95"
                    >
                      Excel Detailed
                    </button>
                  }
                  
                  {group.excel_summarized &&
                    <button 
                      onClick={group.excel_summarized} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ.
                    </button>
                  }
                  
                  
                  { group.excel_extra &&
                    <button 
                      onClick={group.excel_extra} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ. w/ %
                    </button>
                  }
                </div>

              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}